import type { RowData } from "@tanstack/react-table";
import type { RecordColumnDef } from "./column-types";
import type { LucideIcon } from "@/components/icons";
import { isRecordObjectType } from "@shared/records/object-types";
import { resolveColumnIds } from "./columns/column-id";
import type {
  RecordCapabilities,
  RecordFieldDef,
  RecordFilterOption,
  RecordLegacyUrlAdapter,
  RecordObjectDef,
  RecordPresentationMode,
  RecordSeededView,
  RecordSort,
} from "./types";

interface RecordObjectInput<TData extends RowData> {
  id: string;
  label: string;
  allViewName?: string;
  icon?: LucideIcon;
  href?: string;
  switcherGroup?: string;
  urlNamespace?: string;
  fields: RecordFieldDef[];
  fieldAliases?: Record<string, string>;
  columns: RecordColumnDef<TData, unknown>[];
  defaultColumns?: string[];
  defaultSort?: RecordSort;
  defaultPageSize?: number;
  capabilities?: Partial<RecordCapabilities>;
  getRowId: (row: TData) => string;
  getRowHref?: (row: TData) => string | undefined;
  seededViews?: RecordSeededView[];
  legacyUrl?: RecordLegacyUrlAdapter;
  presentationModes?: RecordPresentationMode[];
  defaultPresentationMode?: RecordPresentationMode;
}

const FULL_CAPABILITIES: RecordCapabilities = {
  switcher: true,
  views: true,
  columnEditing: true,
  advancedFilters: true,
  search: true,
  selection: true,
};

export const READ_ONLY_CAPABILITIES: RecordCapabilities = {
  switcher: false,
  views: false,
  columnEditing: false,
  advancedFilters: false,
  search: false,
  selection: false,
};

export function resolveRecordPresentationModes(
  modes: RecordPresentationMode[] | undefined,
): RecordPresentationMode[] {
  return Array.from(
    new Set<RecordPresentationMode>(["table", ...(modes ?? [])]),
  );
}

function filterAffordances(fields: RecordFieldDef[]): {
  quickFilterFields: RecordFieldDef[];
  pinnedFilterFields: RecordFieldDef[];
} {
  return {
    quickFilterFields: fields.filter(
      (field) => field.quickFilter && !field.pinnedFilter,
    ),
    pinnedFilterFields: fields.filter((field) => field.pinnedFilter),
  };
}

export function defineRecordObject<TData extends RowData>(
  input: RecordObjectInput<TData>,
): RecordObjectDef<TData> {
  const capabilities = { ...FULL_CAPABILITIES, ...input.capabilities };
  if (capabilities.views && !isRecordObjectType(input.id)) {
    throw new Error(
      `Record object "${input.id}" must be registered before enabling saved views`,
    );
  }
  const fieldsById = new Map(input.fields.map((field) => [field.id, field]));
  const presentationModes = resolveRecordPresentationModes(
    input.presentationModes,
  );
  const defaultPresentationMode = presentationModes.includes(
    input.defaultPresentationMode ?? "table",
  )
    ? (input.defaultPresentationMode ?? "table")
    : "table";

  return {
    ...input,
    capabilities,
    defaultPageSize: input.defaultPageSize ?? 25,
    defaultColumns: input.defaultColumns ?? resolveColumnIds(input.columns),
    fieldsById,
    ...filterAffordances(input.fields),
    seededViews: input.seededViews ?? [],
    presentationModes,
    defaultPresentationMode,
  };
}

export function withFieldOptions<TData extends RowData>(
  object: RecordObjectDef<TData>,
  options: Record<string, RecordFilterOption[] | undefined>,
): RecordObjectDef<TData> {
  if (Object.keys(options).length === 0) return object;
  const fields = object.fields.map((field) =>
    options[field.id] ? { ...field, options: options[field.id] } : field,
  );
  return {
    ...object,
    fields,
    fieldsById: new Map(fields.map((field) => [field.id, field])),
    ...filterAffordances(fields),
  };
}

export function withRecordColumns<TData extends RowData>(
  object: RecordObjectDef<TData>,
  columns: RecordColumnDef<TData, unknown>[],
): RecordObjectDef<TData> {
  const expectedIds = new Set(resolveColumnIds(object.columns));
  const columnIds = resolveColumnIds(columns);
  const hasSameColumns =
    columnIds.length === expectedIds.size &&
    columnIds.every((columnId) => expectedIds.has(columnId));
  if (!hasSameColumns) {
    throw new Error(
      `Columns for record object "${object.id}" do not match its definition`,
    );
  }
  return { ...object, columns };
}

export function withSeededViews<TData extends RowData>(
  object: RecordObjectDef<TData>,
  seededViews: RecordSeededView[],
): RecordObjectDef<TData> {
  if (seededViews.length === 0) return object;
  return { ...object, seededViews: [...object.seededViews, ...seededViews] };
}
